
import Footer from '@/components/layout/Footer.vue'
import MainAdsSwiper from '@/components/component/MainAdsSwiper.vue'
import { supportedLocales } from '@/core/i18n'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, FreeMode } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'
import { useLang } from '@/hook/useLang'
import { useStore } from '@/store'
import { ServiceType } from '@/router'
import Modal from '@/components/component/Modal.vue'
import { commonApi } from '@/core/api/commonApi'
import { useLoadingStore } from '@/store/loadingStore'
import PassportDBService from '@/core/PassportDBService'
import { useGeofence } from '@/hook/useGeoFence'
import { useDialog } from '@/hook/useDialog'
import exportPoints from '@/assets/data/export-points.json'
import { useI18n } from 'vue-i18n'

export default {
  name: 'MainView',
  components: {
    Swiper,
    Modal,
    SwiperSlide,
    MainAdsSwiper,
    Footer
  },
  setup () {
    const route = useRoute()
    const router = useRouter()

    const lang = useLang()
    const { t } = useI18n()
    const { findFirst, clearAll } = PassportDBService.getInstance()
    const store = useStore()
    const { setLoading } = useLoadingStore()
    const { alertDialog } = useDialog()

    // 가이드 팝업
    const isGuidePopupOpen = ref(false)

    const isReusePassportModalOpen = ref(false) // 여권 정보 불러오기 모달
    const isResetPassportModalOpen = ref(false) // 여권 정보 초기화 모달
    const isCustomsValidationProcessModalOpen = ref(false) // 자동 세관신고 진행 모달

    const loadedPassportName = ref('')
    const selectedService = ref<ServiceType|null>(null)

    const { start, stop } = useGeofence(exportPoints, g => {
      checkWifi(true).then(wifiConnected => {
        if (!wifiConnected) {
          alertDialog({
            title: t('geo_location_alert_1_1'),
            description: t('geo_location_alert_1_2', { location: t(g.key), wifi: g.wifi[0] })
          })
        }
        stop()
      })
    })

    // 암호화된 여권 정보가 존재할 경우 마스킹된 이름 반환
    const checkEncryptedPassport = async () => {
      const encryptedPassport = await findFirst() as any

      if (encryptedPassport) {
        // TODO: 타입 검증 후 맞지 않으면 제거
        const passportName = encryptedPassport.maskedPassportName
        const expiryTime = encryptedPassport.expiryTime
        if (expiryTime > Date.now()) {
          return passportName
        } else {
          await clearAll()
        }
      }
      return null
    }

    const checkWifi = async (withLoading: boolean) => {
      if (withLoading) setLoading(true)
      const wifiAvailable = await commonApi.checkBoothTml()
      if (withLoading) setLoading(false)

      return wifiAvailable
    }

    // 진행할 서비스 선택
    const selectService = async (serviceType: ServiceType) => {
      setLoading(true)
      isCustomsValidationProcessModalOpen.value = false

      // 나의 환급 내역을 제외하고 와이파이 체크
      if (serviceType !== 'history') {
        const wifiConnected = await checkWifi(false)

        if (serviceType === 'terminal' && !wifiConnected) {
          // 출국항 반출 진행 중 와이파이 미연결 시 경고 페이지로 이동
          router.push(`/${lang.value.code}/check-wifi`)
          setLoading(false)
          return
        } else if (serviceType === 'city' && wifiConnected) {
          // 시내/도심 선택 시 인증된 와이파이가 연결되어있을 경우 출국항 반출로 전환
          // serviceType = 'terminal'
          isCustomsValidationProcessModalOpen.value = true
          setLoading(false)
          return
        }
      }

      selectedService.value = serviceType
      try {
        const encryptedPassportName = await checkEncryptedPassport()

        if (encryptedPassportName) {
          loadedPassportName.value = encryptedPassportName
          isReusePassportModalOpen.value = true
        } else {
          router.push(`/${lang.value.code}/${serviceType}/consent/privacy`)
        }
      } catch (e) {
        location.reload()
      }
      setLoading(false)
    }

    // 여권 정보 초기화 후 서비스 진행
    const onClickResetPassport = () => {
      isReusePassportModalOpen.value = false
      isResetPassportModalOpen.value = true
    }

    // 등록된 여권 정보로 서비스 진행
    const startServiceWithSavedData = () => {
      if (selectedService.value) {
        router.push(`/${lang.value.code}/${selectedService.value}/pin/verify`)
      }
    }

    // 등록된 여권 정보를 사용하지 않고 서비스 진행
    const startServiceWithoutSavedData = async () => {
      await clearAll()
      if (selectedService.value) {
        router.push(`/${lang.value.code}/${selectedService.value}/consent/privacy`)
      }
    }

    const goToTaxRefundGuide = () => {
      router.push(`/${lang.value.code}/guide`)
    }
    // 가이드 페이지
    const goToTips = () => {
      router.push(`/${lang.value.code}/tips`)
    }
    // 거래 내역
    const goToRefundHistory = () => selectService('history')
    // 송금 예약
    const goToRemitBook = () => selectService('city')
    // 출국항 반출 진행
    const goToOutRequest = () => selectService('terminal')

    // 광고 클릭
    const openNewTab = (url: string) => window.open(url, '__blank')

    // 가이드 팝업 tail 위치 조정
    const resizeEvent = () => {
      const contentWrap = document.getElementsByClassName('content-wrap')[0] as HTMLElement
      const infoIcon = document.getElementsByClassName('greeting__info--icon')[0] as HTMLElement
      const guideTail = document.getElementsByClassName('guide-popup__tail')[0] as HTMLElement
      const guideBubble = document.getElementsByClassName('guide-popup__bubble')[0] as HTMLElement
      const padding = 15
      const guideBubbleMinXOffset = 5

      const guideTailXOffset = infoIcon.getBoundingClientRect().left - ((window.innerWidth - (contentWrap.getBoundingClientRect().width - (padding * 2)))) / 2
      const guideBubbleXOffset = Math.min(contentWrap.getBoundingClientRect().width - (padding * 2) - guideBubble.getBoundingClientRect().width, guideTailXOffset - guideBubbleMinXOffset)

      guideBubble.style.left = `${guideBubbleXOffset}px`
      guideTail.style.left = `${guideTailXOffset}px`
    }

    // 가이드 팝업 노출 시 배경 클릭으로 자동 닫기
    const guidePopupAutoCloseEvent = (e: Event) => {
      const infoIcon = document.querySelector('.greeting__info--icon') as HTMLElement
      const guidePopup = document.querySelector('.guide-popup') as HTMLElement

      if (!infoIcon.contains(e.target as Node) && !guidePopup.contains(e.target as Node)) {
        isGuidePopupOpen.value = false
      }
    }

    onMounted(() => {
      start()
      store.$reset()

      // 화면 사이즈에 따라 가이드 팝업 tail 위치 조정
      window.addEventListener('resize', resizeEvent)
      // 화면 마운트 시 진행되면 정상적으로 동작하지 않아 딜레이 적용
      setTimeout(() => {
        resizeEvent()
      }, 300)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', resizeEvent)
    })

    watch(isGuidePopupOpen, (open) => {
      if (open) {
        document.addEventListener('click', guidePopupAutoCloseEvent)
      } else {
        document.removeEventListener('click', guidePopupAutoCloseEvent)
      }
    })

    watch(route, () => {
      resizeEvent()
    })

    return {
      lang,
      isGuidePopupOpen,
      isReusePassportModalOpen,
      isResetPassportModalOpen,
      isCustomsValidationProcessModalOpen,
      loadedPassportName,
      goToTaxRefundGuide,
      goToTips,
      goToRefundHistory,
      goToRemitBook,
      goToOutRequest,
      onClickResetPassport,
      startServiceWithSavedData,
      startServiceWithoutSavedData,
      openNewTab,
      modules: [Pagination, FreeMode]
    }
  }
}
